<template>
    <div>
        <EstimateForm @save="onSegmentSave"
                      :estimate="estimate"
                      :segment-configurations-factory="createSegmentConfiguration"/>
        <RecalculationDialog :recalculation-handler="() => model.recalculateWithNewEstates()"
                             ref="recalculationDialog"/>
    </div>
</template>

<script lang="ts">
import {RealEstateEstimateState as EstimateState} from '@/model/api';
import {ParameterList} from '@/model/api/parameters';
import RealEstateEstimate from '@/model/estimates/RealEstateEstimate';
import {HttpClient} from '@/model/http';
import {NotSubmittedParameters, OfflineManager, StagedParameters} from '@/model/offline';
import {StoredParameters} from '@/model/offline/storage/StoredParameters';
import {fromPairs, isEqual} from 'lodash-es';
import {defineComponent, PropType, provide, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import EstimateForm from './common/EstimateForm.vue';
import PhotosSegment from './common/PhotosSegment.vue';
import {LocationSegment, ParametersSegment, SegmentConfiguration} from './common/segments';
import RecalculationDialog from './realEstateEstimate/RecalculationDialog.vue';

export default defineComponent({
    components: {EstimateForm, RecalculationDialog},
    props: {
        estimate: {
            type: Object as PropType<EstimateState>,
            required: true,
        },
    },
    setup(props) {
        const {t} = useI18n();

        const recalculationDialog = ref<InstanceType<typeof RecalculationDialog> | null>(null);

        const notSubmittedParameters = new NotSubmittedParameters(
            new StoredParameters(
                props.estimate.id,
                NotSubmittedParameters.STORAGE_KEY,
            ),
        );

        const model = new RealEstateEstimate(props.estimate, new HttpClient());

        const offlineManager = new OfflineManager<EstimateState>(
            new StagedParameters(
                new StoredParameters(
                    props.estimate.id,
                    StagedParameters.STORAGE_KEY,
                ),
            ),
            model,
        );

        provide('notSubmittedParameters', notSubmittedParameters);
        provide('model', model);
        provide('offlineManager', offlineManager);

        function createSegmentConfiguration(estimate: EstimateState): SegmentConfiguration[] {
            const requiredParameters: ParameterList = {};
            const optionalParameters: ParameterList = {};

            Object.entries(estimate.parameters).forEach(([name, definition]) => {
                (definition.optional ? optionalParameters : requiredParameters)[name] = definition;
            });

            return [
                {
                    label: t('form.segments.location'),
                    path: '/adresa',
                    component: LocationSegment,
                    props: {defaultValue: estimate.location},
                },
                {
                    label: t('form.segments.requiredParameters'),
                    path: '/povinne-parametry',
                    component: ParametersSegment,
                    props: {
                        parameters: requiredParameters,
                        stagedParameters: offlineManager.stagedParameters,
                        notSubmittedParameters,
                    },
                },
                {
                    label: t('form.segments.optionalParameters'),
                    path: '/doplnujici-udaje',
                    component: ParametersSegment,
                    props: {
                        parameters: optionalParameters,
                        stagedParameters: offlineManager.stagedParameters,
                        notSubmittedParameters,
                    },
                },
                {
                    label: t('form.segments.photos'),
                    path: '/fotografie',
                    component: PhotosSegment,
                    props: {photos: estimate.photos, estimate},
                },
            ];
        }

        function onSegmentSave(
            segmentIndex: number, state: EstimateState, lastKnownState: EstimateState,
        ): void {
            if (!recalculationAvailable(segmentIndex, state, lastKnownState)) {
                return;
            }

            recalculationDialog.value!.show();
        }

        function recalculationAvailable(
            segmentIndex: number, state: EstimateState, lastKnownState: EstimateState,
        ): boolean {
            if (state.calculationUrl === null) {
                return false;
            }

            if (segmentIndex === 0) {
                return !isEqual(state.location, lastKnownState.location);
            }

            return !isEqual(getRequiredParameters(state), getRequiredParameters(lastKnownState));
        }

        function getRequiredParameters(estimate: EstimateState): ParameterList {
            return fromPairs(
                Object.entries(estimate.parameters).filter(([, definition]) => !definition.optional),
            );
        }

        return {
            recalculationDialog,
            model,
            createSegmentConfiguration,
            onSegmentSave,
        };
    },
});
</script>
